import type { OrgChartPerson, OrgDepartment, OrgSeat } from '../types/purchasing';
import {
  ORG_LEVEL_RANK,
  isOrgChartBot,
  orgSeatOf,
  resolveOrgPlacement,
} from './orgChart';

/**
 * Reporting rules — the single source of truth for who reports to whom.
 *
 *   CEO                      -> nobody (apex)
 *   COO, CFO (Executive)     -> CEO
 *   Office Manager           -> CEO
 *   1099 contractor pool     -> COO (CEO if there is no COO)
 *   Managerial staff         -> group head (Shop -> COO; Sales, Office -> CEO)
 *   General staff            -> explicit manager when that person is in the same
 *                               group and a higher layer; else the Office Manager
 *                               (Office) or the group's single Managerial person;
 *                               else the group head
 */

type Person = OrgChartPerson;

function byName(a: Person, b: Person): number {
  return a.displayName.localeCompare(b.displayName);
}

export function findSeatHolder(people: Person[], seat: OrgSeat): Person | undefined {
  const holders = people
    .filter(p => p.isActive !== false && !isOrgChartBot(p) && orgSeatOf(p) === seat)
    .sort(byName);
  return holders[0];
}

export function groupExecutiveHead(group: OrgDepartment, people: Person[]): Person | undefined {
  const ceo = findSeatHolder(people, 'ceo');
  if (group === 'shop') return findSeatHolder(people, 'coo') || ceo;
  return ceo;
}

/**
 * Can `manager` be chosen as the explicit manager of `report`? Only General
 * staff take explicit managers; the manager must be in the same group and a
 * strictly higher layer (the CEO counts as head of Sales and Office, and of
 * Shop only when there is no COO on the roster).
 */
export function canManage(manager: Person, report: Person, people: Person[] = [manager, report]): boolean {
  if (manager.uid === report.uid) return false;
  if (manager.isActive === false) return false;
  if (isOrgChartBot(manager)) return false;
  const managerSeat = orgSeatOf(manager);
  const reportSeat = orgSeatOf(report);
  if (managerSeat === 'contractor') return false;
  if (reportSeat !== 'staff') return false;
  const mp = resolveOrgPlacement(manager);
  const rp = resolveOrgPlacement(report);
  const reportLevel = isOrgChartBot(report) ? 'general' : rp.orgLevel;
  if (reportLevel !== 'general') return false;
  if (managerSeat === 'ceo') return groupExecutiveHead(rp.orgDepartment, people)?.uid === manager.uid;
  return mp.orgDepartment === rp.orgDepartment && ORG_LEVEL_RANK[mp.orgLevel] < ORG_LEVEL_RANK[rp.orgLevel];
}

/** People a General staffer may explicitly report to. */
export function managerOptionsFor(report: Person, people: Person[]): Person[] {
  return people.filter(p => canManage(p, report, people)).sort(byName);
}

/** Default manager for a General staffer when no valid explicit manager is set. */
export function defaultManagerFor(report: Person, people: Person[]): Person | undefined {
  const place = resolveOrgPlacement(report);
  if (place.orgDepartment === 'office') {
    const om = findSeatHolder(people, 'office_manager');
    if (om && om.uid !== report.uid) return om;
  }
  const managers = people.filter(p =>
    p.uid !== report.uid
    && p.isActive !== false
    && !isOrgChartBot(p)
    && orgSeatOf(p) !== 'contractor'
    && resolveOrgPlacement(p).orgLevel === 'managerial'
    && resolveOrgPlacement(p).orgDepartment === place.orgDepartment);
  if (managers.length === 1) return managers[0];
  return groupExecutiveHead(place.orgDepartment, people);
}

export function deriveReportsTo(person: Person, people: Person[]): string | undefined {
  const seat = orgSeatOf(person);
  const ceo = findSeatHolder(people, 'ceo');
  const ceoId = ceo && ceo.uid !== person.uid ? ceo.uid : undefined;

  if (seat === 'ceo') return undefined;
  if (seat === 'coo' || seat === 'cfo' || seat === 'office_manager') return ceoId;
  if (seat === 'contractor') {
    const coo = findSeatHolder(people, 'coo');
    return coo?.uid || ceoId;
  }

  const place = resolveOrgPlacement(person);
  if (place.orgLevel === 'managerial') {
    const head = groupExecutiveHead(place.orgDepartment, people);
    return head && head.uid !== person.uid ? head.uid : undefined;
  }

  const explicit = person.reportsToUserId
    ? people.find(p => p.uid === person.reportsToUserId)
    : undefined;
  if (explicit && canManage(explicit, person, people)) return explicit.uid;

  const fallback = defaultManagerFor(person, people);
  return fallback && fallback.uid !== person.uid ? fallback.uid : undefined;
}

/** Stamp `effectiveReportsToUserId` on every person. */
export function applyEffectiveReporting(people: Person[]): Person[] {
  return people.map(p => ({ ...p, effectiveReportsToUserId: deriveReportsTo(p, people) || '' }));
}

export type ReportingSyncRow = {
  uid: string;
  displayName: string;
  from: string | undefined;
  to: string | undefined;
};

/** People whose stored manager differs from the rule-derived manager. */
export function reportingSyncDiff(people: Person[]): ReportingSyncRow[] {
  const rows: ReportingSyncRow[] = [];
  for (const p of people) {
    const to = (p.effectiveReportsToUserId !== undefined
      ? p.effectiveReportsToUserId
      : deriveReportsTo(p, people)) || undefined;
    const from = p.reportsToUserId || undefined;
    if (from !== to) rows.push({ uid: p.uid, displayName: p.displayName, from, to });
  }
  return rows;
}

/** Non-contractor chart people, for the hiring "reports to" picker and similar. */
export function managerCandidates(people: Person[]): Person[] {
  return people.filter(p => orgSeatOf(p) !== 'contractor' && !isOrgChartBot(p)).sort(byName);
}
